import type {
  UserProfileInterface,
  LoginPayload,
} from "../interfaces/auth_interfaces";
import authRepository from "./auth_repository";
import router from "@/router";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import { useDark, useToggle } from "@vueuse/core";
import BaseNotification from "@/new_design/components/storybook/components/Toast/BaseNotification.vue";

export const useAuthStore = defineStore(
  "auth",
  () => {
    const userId = ref<string | undefined>();
    const company = ref<string>();
    const role = ref<string>();
    const toast = useToast();

    const dark = useDark();
    const toggleDark = useToggle(dark);

    const profile = ref<UserProfileInterface>({} as UserProfileInterface);

    const isAuthenticated = computed(
      () => !!(Object.keys(profile.value).length && userId.value)
    );

    const isManager = computed(() =>
      profile.value?.user?.roles?.some((r) => r === "MANAGER")
    );

    //const fbStore = useFirebaseStore();

    const showToast = (status: string, content: string) => {
      const toastContent = {
        component: BaseNotification,
        props: { status: status, content: content },
      };

      const timeoutDuration = status === "success" ? 1000 : 5000;

      toast({ ...toastContent, timeout: timeoutDuration });
    };

    const signIn = async (payload: LoginPayload): Promise<void> => {
      const response = await authRepository.signIn(payload);
      if (
        response.profile.user.roles.some(
          (el) =>
            el === "DRIVER" ||
            el === "TECHNICIAN" ||
            el === "SUPER_ADMIN" ||
            el === "CUSTOMER"
        )
      ) {
        showToast("error", "Your current role does not have access.");
        return;
      } else {
        profile.value = response.profile;
        userId.value = response.profile._id;
        localStorage.setItem("company", response.profile.company);
        company.value = response.profile.company;

        router.push({ name: "loading" });
      }
    };

    const logout = async () => {
      router.replace({ name: "login" });
      Object.keys(profile.value).forEach((k) => {
        delete profile.value[k as keyof UserProfileInterface];
      });
      userId.value = "";
      localStorage.clear();
    };

    const isDispatcher = computed(() =>
      profile.value?.user?.roles?.some((r) => r === "DISPATCHER")
    );

    const showCustomerRate = computed(
      () =>
        !!(
          (isDispatcher.value && profile.value?.showCustomerRate) ||
          isManager.value
        )
    );

    const showArchiveButton = computed(
      () => !!profile.value?.showArchiveButton
    );

    const isEmployee = computed(() =>
      profile.value?.user?.roles?.some((r) => r === "EMPLOYEE")
    );

    // Asegurémonos de que el store se inicialice correctamente
    const initialize = () => {
      const storedUserId = localStorage.getItem("userId");
      const storedCompany = localStorage.getItem("company");

      if (storedUserId) {
        userId.value = storedUserId;
      }
      if (storedCompany) {
        company.value = storedCompany;
      }
    };

    return {
      signIn,
      profile,
      userId,
      isAuthenticated,
      logout,
      isDispatcher,
      isManager,
      showCustomerRate,
      showArchiveButton,
      dark,
      toggleDark,
      isEmployee,
      initialize,
    };
  },
  { persist: true }
);

export default useAuthStore;
